import React from 'react';
import { reportError } from '../utils/errorReporter';

// Catches render-time exceptions. Without this, one bad recipe document --
// a missing field, an unexpected shape -- takes the whole page to a blank
// white screen with the real cause visible only in a console nobody is
// watching. React unmounts the entire tree on an uncaught render error, so
// there is no partial degradation: it's this or nothing.
//
// Still a class component because React provides no hook equivalent of
// componentDidCatch; that isn't legacy code, it's the only supported form.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // componentStack names the component chain, not user data -- safe to
    // send, and it is the single most useful field for locating the bug.
    reportError(error, {
      source: 'react',
      context: (info?.componentStack || '').trim().split('\n')[0] || '',
    });
  }

  handleReload = () => {
    // Full reload rather than resetting state: a render crash usually means
    // the data in memory is the problem, so re-mounting the same tree with
    // the same data tends to crash straight back.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="main-content">
        <div className="empty-state">
          <div className="empty-state-icon" aria-hidden="true">🍳</div>
          <h1 className="empty-state-title">Something went wrong</h1>
          <p>
            That page didn't load properly. The problem has been reported —
            you don't need to do anything.
          </p>
          <div className="error-boundary-actions">
            <button className="btn btn-primary" onClick={this.handleReload}>
              Reload the page
            </button>
            <a className="btn btn-secondary" href="/">
              Back to browse
            </a>
          </div>
        </div>
      </div>
    );
  }
}
